import { subscribeBackIntentChange } from './backIntentBus.js';
import { getEscapeCloseStackSize, invokeTopEscapeClose } from './useEscapeCloseStack.js';
import { getAppBackStackSize, invokeTopAppBack } from './useAppBackButton.js';

/** Marker copied onto history.state so overlay / nested Back can be intercepted. */
export const APP_BACK_GUARD = 'rentalyzerAppBack';

export const APP_BACK_BUTTON_ATTR = 'data-rentalyzer-back';

let installed = false;
let win = null;
let guardDepth = 0;
let pendingSilentPops = 0;
let syncScheduled = false;
let unsubscribeBus = null;
let previousScrollRestoration = null;

function isGuardState(state) {
  return Boolean(state && typeof state === 'object' && state[APP_BACK_GUARD] === true);
}

function queryVisibleAppBackButton() {
  const doc = win?.document;
  if (!doc?.querySelectorAll) return null;
  const nodes = doc.querySelectorAll(`[${APP_BACK_BUTTON_ATTR}]`);
  for (let i = nodes.length - 1; i >= 0; i -= 1) {
    const el = nodes[i];
    if (el && typeof el.click === 'function' && (el.offsetWidth || el.offsetHeight || el.getClientRects?.().length)) {
      return el;
    }
  }
  return nodes.length ? nodes[nodes.length - 1] : null;
}

function needGuard() {
  return (
    getEscapeCloseStackSize() > 0 ||
    getAppBackStackSize() > 0 ||
    Boolean(queryVisibleAppBackButton())
  );
}

function pushGuard() {
  if (!win || guardDepth > 0) return;
  const prev = win.history.state;
  const prevObj = prev && typeof prev === 'object' && !Array.isArray(prev) ? prev : {};
  win.history.pushState(
    { ...prevObj, [APP_BACK_GUARD]: true },
    '',
    win.location.href,
  );
  guardDepth = 1;
}

function popGuardSilently() {
  if (!win || guardDepth === 0) return;
  guardDepth = 0;
  if (!isGuardState(win.history.state)) return;
  pendingSilentPops += 1;
  win.history.back();
}

function syncGuard() {
  if (!win) return;
  const need = needGuard();
  if (need && guardDepth === 0) pushGuard();
  else if (!need && guardDepth > 0) popGuardSilently();
}

function scheduleSync() {
  if (syncScheduled) return;
  syncScheduled = true;
  queueMicrotask(() => {
    syncScheduled = false;
    syncGuard();
  });
}

function clickVisibleAppBackButton() {
  const btn = queryVisibleAppBackButton();
  if (!btn) return false;
  btn.click();
  return true;
}

function onPopState(event) {
  if (pendingSilentPops > 0) {
    pendingSilentPops -= 1;
    return;
  }

  const intercept =
    getEscapeCloseStackSize() > 0 ||
    getAppBackStackSize() > 0 ||
    Boolean(queryVisibleAppBackButton());

  if (!intercept) {
    guardDepth = 0;
    return;
  }

  // Same-URL dummy pops restore Chrome's previous scroll (top of page) and can
  // be swallowed by HashRouter. Re-trap immediately and handle it ourselves.
  event?.stopImmediatePropagation?.();
  guardDepth = 0;
  pushGuard();

  if (invokeTopEscapeClose()) {
    scheduleSync();
    return;
  }
  if (clickVisibleAppBackButton()) {
    scheduleSync();
    return;
  }
  if (invokeTopAppBack()) {
    scheduleSync();
  }
}

/**
 * Intercepts Android/browser Back for overlays and nested views that do not
 * change the HashRouter URL. Main screens use `?rz=` history instead.
 */
export function installAppBackHistory(targetWindow) {
  const next = targetWindow || (typeof window !== 'undefined' ? window : null);
  if (!next?.history || installed) return;
  win = next;
  installed = true;
  if (next.history.scrollRestoration) {
    previousScrollRestoration = next.history.scrollRestoration;
    next.history.scrollRestoration = 'manual';
  }
  next.addEventListener('popstate', onPopState, true);
  unsubscribeBus = subscribeBackIntentChange(scheduleSync);
  scheduleSync();
}

export function resetAppBackHistoryForTests() {
  if (win) {
    win.removeEventListener('popstate', onPopState, true);
    if (previousScrollRestoration && win.history.scrollRestoration) {
      win.history.scrollRestoration = previousScrollRestoration;
    }
  }
  unsubscribeBus?.();
  unsubscribeBus = null;
  installed = false;
  win = null;
  guardDepth = 0;
  pendingSilentPops = 0;
  syncScheduled = false;
  previousScrollRestoration = null;
}

export function getAppBackGuardDepthForTests() {
  return guardDepth;
}
